import { useEffect, useState } from 'react';

import { trackEvent } from '@/utils/analytics';
import { browserPlatform } from '@/utils/browser-platform';

import { subscriptionApis } from '../subscription.query';
import { subscriptionStore } from '../subscription.store';

/** Query keys the backend adds to the web return URL of Checkout and the billing portal */
export const STRIPE_RETURN_KEYS = ['checkout', 'client'] as const;

const PLAN_CHECKS = 6;
const PLAN_CHECK_INTERVAL_MS = 1500;
const PORTAL_RECHECK_MS = 3000;

type StripeReturn = 'success' | 'cancelled' | 'portal';

type CheckoutReturn = { stripeReturn: StripeReturn; client: string };

/** A return from Stripe to subscription settings; the channel dashboard reads its own returns */
function readCheckoutReturn(): CheckoutReturn | null {
  if (window.location.pathname.startsWith('/channels')) return null;
  const params = new URLSearchParams(window.location.search);
  const stripeReturn = params.get('checkout');
  if (
    stripeReturn !== 'success' &&
    stripeReturn !== 'cancelled' &&
    stripeReturn !== 'portal'
  ) {
    return null;
  }
  return { stripeReturn, client: params.get('client') ?? 'unknown' };
}

function clearCheckoutReturn() {
  const url = new URL(window.location.href);
  for (const key of STRIPE_RETURN_KEYS) url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.toString());
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Refreshes the plan after Stripe sends the user back to the web app, until a paid checkout shows */
export function useCheckoutReturnLanding() {
  const [landed] = useState(readCheckoutReturn);

  useEffect(() => {
    if (!landed) return;
    clearCheckoutReturn();
    trackEvent('CHECKOUT_RETURN_LANDED', {
      client: landed.client,
      platform: browserPlatform(),
      destination: 'web',
      stripe_return: landed.stripeReturn,
    });
    let stopped = false;
    void (async () => {
      await subscriptionApis.getStatus();
      if (landed.stripeReturn === 'portal') {
        await wait(PORTAL_RECHECK_MS);
        if (!stopped) await subscriptionApis.getStatus();
        return;
      }
      if (landed.stripeReturn !== 'success') return;
      // The webhook that activates the plan can land after the redirect
      for (let check = 0; check < PLAN_CHECKS; check++) {
        if (stopped || subscriptionStore.isActive) return;
        await wait(PLAN_CHECK_INTERVAL_MS);
        if (!stopped) await subscriptionApis.getStatus();
      }
    })();
    return () => {
      stopped = true;
    };
  }, [landed]);
}
